import React, { lazy, Suspense, useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { withChalkTalkErrorBoundary } from '@components/ChalkTalkErrorBoundary/ChalkTalkErrorBoundary';
import { ReportsPlaceholder } from '../ReportHarness/styles';

const ReportsHarness = lazy(() =>
  import('../ReportHarness').then((module) => ({ default: module.default })),
);

interface ResponseAnalysisByItemReportProps {
  teacherId: string;
  reportId: string;
  users: { id: string; name: string }[];
  sessionIds: string[];
  itemReferenceMap: { reference: string; name: string }[];
  configProperties?: any;
  usersNotAttempted?: { id: string; name: string }[];
}

// Time in milliseconds to wait before injecting missing student rows
// Needed to ensure that the Learnosity report has fully rendered before we manipulate the DOM
const JS_INJECTION_TIMEOUT = 500;

const ResponseAnalysisByItemReport = ({
  teacherId,
  reportId,
  users,
  sessionIds,
  itemReferenceMap,
  configProperties = {},
  usersNotAttempted = [],
}: ResponseAnalysisByItemReportProps) => {
  const [widget, setWidget] = useState<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const reportData = useMemo(
    () => [
      {
        id: reportId,
        type: 'response-analysis-by-item',
        users,
        session_ids: sessionIds,
        item_reference_map: itemReferenceMap,
        user_id: teacherId,
        ...configProperties,
      },
    ],
    [configProperties, itemReferenceMap, reportId, sessionIds, users, teacherId],
  );

  const renderReport =
    !!reportData[0]?.id &&
    !!reportData[0]?.users?.length &&
    !!reportData[0]?.session_ids?.length &&
    !!teacherId;

  const dataListener = useCallback(() => {

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!renderReport || usersNotAttempted.length === 0) {
      return;
    }

    intervalRef.current = setInterval(() => {
      const table = document.querySelector('.response-analysis-by-item .lrn-ra-main-body');
      if (!table) {
        return;
      }

      usersNotAttempted.forEach((student) => {
        const studentColumn = table.querySelector(".lrn-ra-body-student");
        const pointsColumn = table.querySelector(".lrn-ra-body-points");

        if (studentColumn && !studentColumn.querySelector(`[data-user_id="${student.id}"]`)) {
          const row = document.createElement('div');
          row.className = 'lrn-ra-body-student-name';
          row.dataset.user_id = student.id;
          const nameSpan = document.createElement('span');
          nameSpan.textContent = student.name;
          row.appendChild(nameSpan);
          table.querySelector('.lrn-ra-body-student')?.appendChild(row);
        }

        if (pointsColumn && !pointsColumn.querySelector(`[data-user_id="${student.id}"]`)) {
          const pointsRow = document.createElement('div');
          pointsRow.className = 'lrn-ra-body-points-student';
          pointsRow.dataset.user_id = student.id;
          const statusSpan = document.createElement('span');
          statusSpan.textContent = 'No Attempt';
          pointsRow.appendChild(statusSpan);
          pointsColumn?.appendChild(pointsRow);
        }
      });
    }, JS_INJECTION_TIMEOUT);
  }, [usersNotAttempted, renderReport]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const callbacks = useMemo(() => ({ dataListener }), [dataListener]);

  if (!renderReport) {
    return (
      <ReportsPlaceholder>
        <span>No Reports Available!</span>
      </ReportsPlaceholder>
    );
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ReportsHarness
        key={reportId}
        widget={widget}
        userId={teacherId}
        setWidget={setWidget}
        reports={reportData}
        reportId={reportId}
        callbacks={callbacks}
      />
    </Suspense>
  );
};

const wrappedResponseAnalysisByItemReport = withChalkTalkErrorBoundary(
  ResponseAnalysisByItemReport,
);
export {
  wrappedResponseAnalysisByItemReport as ResponseAnalysisByItemReport,
  ResponseAnalysisByItemReportProps,
};
